import { Router as RouterProps, withRouter } from "next/router"
import React from "react"
import { compose } from "react-apollo"
import { getMuiTheme } from "../../../setup/muiTheme"
import { Maybe, MerchantInfoQueryComponent, MerchantInfoQueryQuery } from "../../../../graphql/generated-types"
import { getLocationInfo, getLocationSettings } from "../../../../utils/data"
import { ThemeProvider } from "@material-ui/styles"
import chroma from "chroma-js"
import { getActiveAppSection } from "../../../../utils/router"
import { isHexColorValid } from "../../../../utils/general"
import { defaultMibColors } from "../../../../utils/style"

interface Props {
    displayPreloading: boolean
    children: React.ReactNode
    router: RouterProps
}

class MuiThemeProvider extends React.PureComponent<Props> {
    componentDidMount() {
        // Remove the server-side injected CSS. -
        const jssStyles = document.querySelector("#jss-server-side")
        if (jssStyles) {
            jssStyles.parentNode.removeChild(jssStyles)
        }
    }

    setCssVarColors = (data: Maybe<MerchantInfoQueryQuery>) => {
        // Set css variables colors. Used for generating Tailwind classnames such as: 'text-primary' etc.
        const color = data?.merchantInfo ? getLocationInfo(data.merchantInfo)?.settings.primaryColor : null
        if (isHexColorValid(color)) {
            const primary = chroma(color).rgb()
            const lighter = chroma(color).brighten(0.25).rgb()
            const darker = chroma(color).darken(0.25).saturate(0.5).rgb()
            document?.documentElement?.style.setProperty(
                defaultMibColors.primary.cssVarName,
                `${primary[0]} ${primary[1]} ${primary[2]}`
            )
            document?.documentElement?.style.setProperty(
                defaultMibColors.dark.cssVarName,
                `${darker[0]} ${darker[1]} ${darker[2]}`
            )
            document?.documentElement?.style.setProperty(
                defaultMibColors.light.cssVarName,
                `${lighter[0]} ${lighter[1]} ${lighter[2]}`
            )
        }
    }

    render() {
        if (this.props.displayPreloading || !getActiveAppSection(this.props.router)) {
            return <ThemeProvider theme={getMuiTheme()}>{this.props.children}</ThemeProvider>
        } else {
            return (
                <MerchantInfoQueryComponent
                    fetchPolicy="cache-and-network"
                    onCompleted={(data: Maybe<MerchantInfoQueryQuery>) => {
                        this.setCssVarColors(data)
                    }}>
                    {({ error, data }) => {
                        const noData = !data || !data.merchantInfo || error
                        const locationInfo = !noData && getLocationInfo(data.merchantInfo)
                        const locationSettings = locationInfo && getLocationSettings(locationInfo)

                        const theme = locationSettings ? getMuiTheme(locationSettings.primaryColor) : getMuiTheme()
                        this.setCssVarColors(data)
                        return (
                            <div>
                                <ThemeProvider theme={theme}>{this.props.children}</ThemeProvider>
                                {Boolean(locationSettings && locationSettings.primaryColor) && (
                                    <style jsx={true} global={true}>{`
                                        #nprogress .bar {
                                            background: ${chroma(locationSettings.primaryColor).hex()};
                                        }
                                    `}</style>
                                )}
                            </div>
                        )
                    }}
                </MerchantInfoQueryComponent>
            )
        }
    }
}

const composed = compose(withRouter)

export default composed(MuiThemeProvider)
